"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { AccordionSectionComponent } from "../../types";
import { isParagraphHtml, sanitizeParagraphHtml } from "../../utils/paragraphHtml";

function renderParagraph(p: string, i: number) {
  if (isParagraphHtml(p)) {
    return (
      <p
        key={i}
        className="rich-paragraph-block"
        dangerouslySetInnerHTML={{ __html: sanitizeParagraphHtml(p) }}
      />
    );
  }
  return <p key={i}>{p}</p>;
}

/** Accordion section renderer with individual panel open/close. */
export function AccordionSectionRenderer({ component }: { component: AccordionSectionComponent }) {
  const { items } = component.content;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!items?.length) return null;

  return (
    <section className="space-y-4" aria-label="Accordion">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const paragraphs = item.paragraphs ?? [];

        return (
          <div
            key={index}
            className={`overflow-hidden rounded-xl border bg-white transition ${
              isOpen ? "border-l-4 border-black/10 border-l-[#00B862] shadow-[var(--shadow-e1)]" : "border-black/10 hover:border-[#00B862]/40"
            }`}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
              aria-expanded={isOpen}
              aria-controls={`accordion-body-${index}`}
              id={`accordion-heading-${index}`}
            >
              <span className="text-base font-semibold text-[#0A0A0A] md:text-lg">
                {item.title || `Item ${index + 1}`}
              </span>
              <span
                className={`shrink-0 text-[#00B862] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                aria-hidden
              >
                <ChevronDown className="h-5 w-5" />
              </span>
            </button>
            <div
              id={`accordion-body-${index}`}
              role="region"
              aria-labelledby={`accordion-heading-${index}`}
              hidden={!isOpen}
              className="border-t border-black/5"
            >
              {isOpen && paragraphs.length > 0 && (
                <div className="rich-paragraph-block space-y-3 px-4 py-4 text-base text-[#121212] xl:text-lg">
                  {paragraphs.map(renderParagraph)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
